// RightSidebar.tsx

import React from "react";
// Ensure LucideIcon is explicitly imported for IconType if it's not exported from Types
import {
  User,
  FileText,
  CheckSquare,
  X,
  Phone,
  Video,
  Mail,
  Calendar,
  ClipboardList,
  LucideIcon,
} from "lucide-react";
// Import custom types
import { Contact, RightSidebarTab,  IconType } from "../Types";
import { Avatar } from "../Avatar";
import SimpleBar from "simplebar-react";
import Notes from "../ContactActions/Notes";
import Task from "../ContactActions/Tasks";

// --- Tab Button Component ---
interface TabButtonProps {
  // IconType is now correctly defined as LucideIcon in Types.ts
  icon: IconType;
  label: string;
  tabName: RightSidebarTab;
  activeTab: RightSidebarTab;
  onClick: (tab: RightSidebarTab) => void;
}

const TabButton: React.FC<TabButtonProps> = ({
  icon: Icon,
  label,
  tabName,
  activeTab,
  onClick,
}) => (
  <button
    onClick={() => onClick(tabName)}
    className={`flex-1 flex items-center justify-center py-2 px-3 text-sm font-semibold transition-colors rounded-t-md ${
      activeTab === tabName
        ? "bg-gray-50 text-blue-600 border-b-2 border-blue-600 dark:bg-gray-800 dark:text-blue-400 dark:border-blue-400"
        : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 border-b-2 border-transparent hover:bg-gray-100 dark:hover:bg-gray-700"
    }`}
  >
    {/* 'Icon' is the variable name for the component prop 'icon' */}
    <Icon className="w-4 h-4 mr-2" />
    {label}
  </button>
);

// --- Info Field Component (for Contact Info) ---
interface InfoFieldProps {
  icon: IconType;
  label: string;
  value: string;
  isLink?: boolean;
  linkPrefix?: string;
}

const InfoField: React.FC<InfoFieldProps> = ({
  icon: Icon,
  label,
  value,
  isLink = false,
  linkPrefix = "",
}) => (
  <div className="flex items-center space-x-3">
    <Icon className="w-5 h-5 text-gray-400 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
        {label}
      </p>
      {isLink ? (
        <a
          href={`${linkPrefix}${value}`}
          className="text-sm font-medium text-blue-600 hover:text-blue-700 truncate transition-colors dark:text-blue-300"
        >
          {value}
        </a>
      ) : (
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {value}
        </p>
      )}
    </div>
  </div>
);

// --- Contact Info Tab Content ---
interface ContactInfoTabProps {
  contact: Contact;
}

const ContactInfoTab: React.FC<ContactInfoTabProps> = ({ contact }) => (
  <div className="space-y-6">
    {/* Contact Card */}
    <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 text-center">
      <div className="mx-auto w-fit">
        <Avatar
          firstName={contact.firstName}
          lastName={contact.lastName}
          isActive={false}
          size="large"
        />
      </div>
      <h4 className="mt-4 text-xl font-bold text-gray-900 dark:text-white">
        {contact.firstName} {contact.lastName}
      </h4>
      <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
        {contact.company}
      </p>
    </div>

    {/* Details Card */}
    <div className="p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 space-y-4">
      <h5 className="font-bold text-gray-900 dark:text-white border-b pb-2 border-gray-100 dark:border-gray-700">
        Contact Details
      </h5>
      <InfoField
        icon={Phone}
        label="Phone"
        value={contact.phone}
        isLink={true}
        linkPrefix="tel:"
      />
      <InfoField
        icon={Mail}
        label="Email"
        value={contact.email}
        isLink={true}
        linkPrefix="mailto:"
      />
      {/* Note: In a real app, 'Member Since' would be passed in via props */}
      <InfoField
        icon={Calendar}
        label="Member Since"
        value={new Date().toLocaleDateString()}
      />
    </div>

    {/* Actions */}
    <div className="pt-2">
      <h5 className="font-bold text-gray-900 dark:text-white mb-3">
        Quick Actions
      </h5>
      <div className="grid grid-cols-2 gap-3">
        <button className="flex items-center justify-center py-2.5 px-3 bg-blue-50 border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium dark:bg-gray-700 dark:border-gray-600 dark:text-blue-300">
          <Calendar className="w-4 h-4 mr-1" /> Schedule
        </button>
        <button className="flex items-center justify-center py-2.5 px-3 bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium dark:bg-gray-700 dark:border-gray-600 dark:text-red-300">
          <X className="w-4 h-4 mr-1" /> Close Deal
        </button>
      </div>
    </div>
  </div>
);

// --- Notes Tab Content ---
interface NotesTabProps {
}

const NotesTab: React.FC<NotesTabProps> = ({
}) => (
  <div className="space-y-4">
    {/* Add Note Section */}
    <div className="p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 space-y-3">
      <h4 className="font-bold text-gray-900 dark:text-white">Add New Note</h4>
    </div>

  </div>
);

// --- Tasks Tab Content ---
interface TasksTabProps {
}

const TasksTab: React.FC<TasksTabProps> = ({
}) => (
  <div className="space-y-4">
    {/* Add Task Section */}
    <div className="p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 space-y-3">
      <h4 className="font-bold text-gray-900 dark:text-white">Add New Task</h4>
    </div>

  </div>
);

// --- Right Sidebar Main Component ---
// The activeConversation type in the component props is slightly ambiguous.
// Assuming the parent component passes a structure like { contact: Contact, otherData: ... }
// Based on usage in RightSidebarContent, 'activeConversation' is a structure containing a 'contact' property.
interface ActiveConversationContainer {
  contactId: Contact;
  // Add other fields that might be on activeConversation if necessary
}

interface RightSidebarProps {
  activeConversation: ActiveConversationContainer | null; // Changed Contact | null to ActiveConversationContainer | null
  activeRightTab: RightSidebarTab;
  setActiveRightTab: (tab: RightSidebarTab) => void;
  setIsSidebarOpen: (isOpen: boolean) => void;
}

const RightSidebarContent: React.FC<
  Omit<RightSidebarProps, "setIsSidebarOpen">
> = (props) => {
  const {
    activeConversation,
    activeRightTab,
  } = props;

  // Check if activeConversation is null or undefined
  if (!activeConversation) return null;

  switch (activeRightTab) {
    case "info":
      // Pass the inner 'contact' object to ContactInfoTab
      return <ContactInfoTab contact={activeConversation.contactId} />;
    case "notes":
      return (
        <Notes contact={activeConversation.contactId}
        />
      );
    case "tasks":
      return (
       <Task contact={activeConversation.contactId}
        />
      );
    default:
      return null;
  }
};

export const RightSidebar: React.FC<RightSidebarProps> = ({
  activeConversation,
  activeRightTab,
  setActiveRightTab,
  setIsSidebarOpen,
  ...contentProps
}) => {
  // Hide the sidebar when no conversation is active
  if (!activeConversation) return null;

  return (
    // Added conditional width/visibility logic for a real-world scenario
    <div
      className={`w-80 border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800 flex flex-col shadow-sm transition-all duration-150 ease-in-out`}
    >
      {/* Sidebar Header with Tabs */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-gray-900 dark:text-white">
            Contact Actions
          </h3>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700">
          <TabButton
            icon={User}
            label="Info"
            tabName="info"
            activeTab={activeRightTab}
            onClick={setActiveRightTab}
          />
          <TabButton
            icon={FileText}
            label="Notes"
            tabName="notes"
            activeTab={activeRightTab}
            onClick={setActiveRightTab}
          />
          <TabButton
            icon={ClipboardList}
            label="Tasks"
            tabName="tasks"
            activeTab={activeRightTab}
            onClick={setActiveRightTab}
          />
        </div>
      </div>

      {/* Sidebar Content */}
      <SimpleBar className="h-full max-h-[calc(100vh_-_330px)] xl:max-h-[calc(100vh_-_280px)] bg-gray-50 dark:bg-gray-900">
        <div className="flex-1 p-4 ">
          {/* The Omit<...> type ensures only the required content props are passed */}
          <RightSidebarContent
            setActiveRightTab={() => {}}
            activeConversation={activeConversation}
            activeRightTab={activeRightTab}
            {...contentProps}
          />
        </div>
      </SimpleBar>
    </div>
  );
};
